/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type AwcAutocompleteElement from '@ambiki/impulse-view-components/dist/elements/autocomplete';
import {
  CreatePaymentMethodCardData,
  loadStripe,
  Stripe,
  StripeCardCvcElement,
  StripeCardExpiryElement,
  StripeCardNumberElement,
  StripeElementChangeEvent,
} from '@stripe/stripe-js';
import axios from 'axios';
import { stripeInputFieldStyles } from 'lib/subscriptions/helpers';

export default class StripeSetup {
  private readonly orgId: string;
  stripe: Stripe | null;
  private cardNumberStatus = { empty: true, complete: false };
  private cardExpiryStatus = { empty: true, complete: false };
  private cardCvcStatus = { empty: true, complete: false };

  form: HTMLFormElement;
  cardNumberField: HTMLElement;
  cardNumberValidationField: HTMLElement;
  cardExpirationField: HTMLElement;
  cardExpirationValidationField: HTMLElement;
  cardCvcField: HTMLElement;
  cardCvcValidationField: HTMLElement;
  nameOnCardInput: HTMLInputElement;
  addressInput: HTMLInputElement;
  cityInput: HTMLInputElement;
  stateInput: AwcAutocompleteElement;
  zipCodeInput: HTMLInputElement;
  emailInput: HTMLInputElement;
  telephoneInput: HTMLInputElement;
  phoneNumberTypeInput: AwcAutocompleteElement;

  cardNumber: StripeCardNumberElement;
  cardExpiry: StripeCardExpiryElement;
  cardCvc: StripeCardCvcElement;

  constructor(orgId: string) {
    this.orgId = orgId;
    this.form = document.querySelector<HTMLFormElement>('#subscription_form')!;
    this.cardNumberField = document.getElementById('card_number')!;
    this.cardNumberValidationField = document.getElementById('card_number_invalid')!;
    this.cardExpirationField = document.getElementById('card_expiration')!;
    this.cardExpirationValidationField = document.getElementById('card_expiration_invalid')!;
    this.cardCvcField = document.getElementById('card_cvc')!;
    this.cardCvcValidationField = document.getElementById('card_cvc_invalid')!;
    this.nameOnCardInput = document.querySelector<HTMLInputElement>('#name_on_card')!;
    this.addressInput = document.querySelector<HTMLInputElement>('#address')!;
    this.cityInput = document.querySelector<HTMLInputElement>('#city')!;
    this.stateInput = document.querySelector<AwcAutocompleteElement>('#state_autocomplete')!;
    this.zipCodeInput = document.querySelector<HTMLInputElement>('#zip_code')!;
    this.emailInput = document.querySelector<HTMLInputElement>('#email')!;
    this.telephoneInput = document.querySelector<HTMLInputElement>('#telephone')!;
    this.phoneNumberTypeInput = document.querySelector<AwcAutocompleteElement>('#phone_number_type_autocomplete')!;
  }

  async initialize() {
    try {
      const { data } = await axios.get<{ pk: string }>('/api/v2/connection-credentials/stripe');
      this.stripe = await loadStripe(data.pk);
      await this.initializeStripeElements();
    } catch {
      this.stripe = null;
      throw new Error();
    }
  }

  private async initializeStripeElements() {
    if (!this.stripe) return;

    const { data } = await this.prepareCustomerPaymentMethod();
    const elements = this.stripe.elements({ clientSecret: data.cs });

    const { style, classes } = stripeInputFieldStyles();
    const cardNumber = elements.create('cardNumber', { style, classes });
    const cardExpiry = elements.create('cardExpiry', { style, classes });
    const cardCvc = elements.create('cardCvc', { style, classes });

    cardNumber.mount(this.cardNumberField);
    cardExpiry.mount(this.cardExpirationField);
    cardCvc.mount(this.cardCvcField);

    cardNumber.on('change', this.handleInputChange.bind(this));
    cardExpiry.on('change', this.handleInputChange.bind(this));
    cardCvc.on('change', this.handleInputChange.bind(this));

    this.cardNumber = cardNumber;
    this.cardExpiry = cardExpiry;
    this.cardCvc = cardCvc;
  }

  private prepareCustomerPaymentMethod() {
    return axios.post<{ cs: string }>('/api/v2/customer-payment-methods/prepare', { organization_id: this.orgId });
  }

  private handleInputChange(event: StripeElementChangeEvent) {
    switch (event.elementType) {
      case 'cardNumber':
        this.cardNumberValidationField.textContent = event.error?.message || '';
        this.cardNumberField.classList.toggle('is-invalid', !!event.error);
        this.cardNumberStatus = { empty: event.empty, complete: event.complete };
        break;
      case 'cardExpiry':
        this.cardExpirationValidationField.textContent = event.error?.message || '';
        this.cardExpirationField.classList.toggle('is-invalid', !!event.error);
        this.cardExpiryStatus = { empty: event.empty, complete: event.complete };
        break;
      case 'cardCvc':
        this.cardCvcValidationField.textContent = event.error?.message || '';
        this.cardCvcField.classList.toggle('is-invalid', !!event.error);
        this.cardCvcStatus = { empty: event.empty, complete: event.complete };
        break;
      default:
      // We don't handle this unknown field.
    }
  }

  checkValidity() {
    if (this.cardNumberStatus.empty) {
      this.cardNumberField.classList.add('is-invalid');
      this.cardNumberValidationField.innerText = 'Your card number is blank';
    }

    if (this.cardExpiryStatus.empty) {
      this.cardExpirationField.classList.add('is-invalid');
      this.cardExpirationValidationField.innerText = "Your card's expiration is blank";
    }

    if (this.cardCvcStatus.empty) {
      this.cardCvcField.classList.add('is-invalid');
      this.cardCvcValidationField.innerText = "Your card's CVC is blank";
    }
  }

  toggleFormDisability(disabled: boolean) {
    this.nameOnCardInput.disabled = disabled;
    this.addressInput.disabled = disabled;
    this.cityInput.disabled = disabled;
    this.stateInput.disabled = disabled;
    this.zipCodeInput.disabled = disabled;
    this.emailInput.disabled = disabled;
    this.telephoneInput.disabled = disabled;
    this.phoneNumberTypeInput.disabled = disabled;
    // this.referralNameInput.disabled = disabled;
    // Card number
    this.cardNumber.update({ disabled });
    this.cardNumberField.toggleAttribute('disabled', disabled);
    // Card expiration
    this.cardExpiry.update({ disabled });
    this.cardExpirationField.toggleAttribute('disabled', disabled);
    // Card cvc
    this.cardCvc.update({ disabled });
    this.cardCvcField.toggleAttribute('disabled', disabled);
  }

  get formValid() {
    return (
      this.form.checkValidity() &&
      !this.cardNumberStatus.empty &&
      !this.cardExpiryStatus.empty &&
      !this.cardCvcStatus.empty &&
      this.cardNumberStatus.complete &&
      this.cardExpiryStatus.complete &&
      this.cardCvcStatus.complete
    );
  }

  get paymentMethodAttrs(): Omit<CreatePaymentMethodCardData, 'type'> {
    return {
      card: this.cardNumber,
      billing_details: {
        email: this.emailInput.value,
        phone: this.telephoneInput.value,
        name: this.nameOnCardInput.value,
        address: {
          line1: this.addressInput.value,
          city: this.cityInput.value,
          state: this.stateInput.querySelector('[data-behavior="hidden-field"]')?.getAttribute('data-text') || '', // Get the abbreviation.
          country: 'us',
          postal_code: this.zipCodeInput.value,
        },
      },
    };
  }
}
